// Favorites saved in this browser (localStorage). Hearts on product cards
// toggle a product handle; the header badge and drawer list follow changes.
const STORAGE_KEY = 'maison-df-chic-favorites';
const CHANGE_EVENT = 'favorites:change';

function readFavorites() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string') : [];
  } catch {
    return [];
  }
}

function writeFavorites(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch {
    // Storage can be unavailable in private browsing; the page still works.
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles } }));
}

function toggleFavorite(handle) {
  const handles = readFavorites();
  const index = handles.indexOf(handle);
  if (index === -1) handles.unshift(handle);
  else handles.splice(index, 1);
  writeFavorites(handles);
  return index === -1;
}

function removeFavorite(handle) {
  writeFavorites(readFavorites().filter((item) => item !== handle));
}

function rootUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

class SFavoriteToggle extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('click', (event) => {
      if (!event.target.closest('button')) return;
      event.preventDefault();
      event.stopPropagation();
      toggleFavorite(this.dataset.handle);
    }, opts);
    document.addEventListener(CHANGE_EVENT, () => this.#render(), opts);
    window.addEventListener('storage', () => this.#render(), opts);
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #render() {
    const button = this.querySelector('button');
    if (!button) return;
    const active = readFavorites().includes(this.dataset.handle);
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', active ? this.dataset.labelRemove : this.dataset.labelAdd);
  }
}

class SFavoritesCount extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    document.addEventListener(CHANGE_EVENT, () => this.#render(), opts);
    window.addEventListener('storage', () => this.#render(), opts);
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #render() {
    const count = readFavorites().length;
    const badge = this.querySelector('[data-favorites-badge]');
    if (badge) {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    }
    const button = this.querySelector('button');
    if (button && this.dataset.label) button.setAttribute('aria-label', `${this.dataset.label} (${count})`);
  }
}

class SFavoritesList extends HTMLElement {
  #ac;
  #cache = new Map();
  #renderId = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    document.addEventListener(CHANGE_EVENT, () => this.#render(), opts);
    window.addEventListener('storage', () => this.#render(), opts);
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-favorite]');
      if (!remove) return;
      removeFavorite(remove.dataset.removeFavorite);
    }, opts);
    const dialog = this.closest('dialog');
    dialog?.addEventListener('toggle', () => { if (dialog.open) this.#render(); }, opts);
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #product(handle) {
    if (this.#cache.has(handle)) return this.#cache.get(handle);
    const request = fetch(`${rootUrl()}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    this.#cache.set(handle, request);
    return request;
  }

  #formatPrice(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency }).format(cents / 100);
  }

  async #render() {
    const renderId = ++this.#renderId;
    const handles = readFavorites();
    const list = this.querySelector('[data-favorites-items]');
    const empty = this.querySelector('[data-favorites-empty]');
    if (!list || !empty) return;

    if (!handles.length) {
      list.replaceChildren();
      empty.hidden = false;
      return;
    }

    const products = await Promise.all(handles.map((handle) => this.#product(handle)));
    if (renderId !== this.#renderId) return;

    const items = [];
    products.forEach((product, index) => {
      if (!product) return;
      items.push(this.#item(product, handles[index]));
    });
    list.replaceChildren(...items);
    empty.hidden = items.length > 0;
  }

  #item(product, handle) {
    const li = document.createElement('li');
    li.className = 'flex items-center gap-3 py-3';

    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'flex min-w-0 flex-1 items-center gap-3 hover:opacity-80';

    if (product.featured_image) {
      const img = document.createElement('img');
      const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '160');
      img.src = url.toString();
      img.alt = '';
      img.width = 64;
      img.height = 85;
      img.loading = 'lazy';
      img.className = 'h-[85px] w-16 shrink-0 rounded-card object-cover bg-surface-secondary';
      link.append(img);
    }

    const text = document.createElement('span');
    text.className = 'flex min-w-0 flex-col gap-1';
    const title = document.createElement('span');
    title.className = 'line-clamp-2 text-sm text-on-surface';
    title.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'text-sm font-medium text-on-surface';
    price.textContent = this.#formatPrice(product.price);
    text.append(title, price);
    link.append(text);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.dataset.removeFavorite = handle;
    remove.className = 'inline-flex size-touch-target shrink-0 items-center justify-center rounded text-on-surface-secondary hover:text-on-surface';
    remove.setAttribute('aria-label', `${this.dataset.labelRemove || ''}: ${product.title}`);
    remove.innerHTML = '<svg class="size-5" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

    li.append(link, remove);
    return li;
  }
}

if (!customElements.get('s-favorite-toggle')) customElements.define('s-favorite-toggle', SFavoriteToggle);
if (!customElements.get('s-favorites-count')) customElements.define('s-favorites-count', SFavoritesCount);
if (!customElements.get('s-favorites-list')) customElements.define('s-favorites-list', SFavoritesList);
